import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, PHONE, PHONE_HREF, scrollToSection } from "./data";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled
            ? "border-b border-white/10 bg-navy-dark/85 shadow-[0_14px_44px_-18px_rgba(0,0,0,0.65)] backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <a
            href="#home"
            data-testid="nav-logo"
            aria-label="Teo's Painting LLC — home"
            onClick={(e) => go(e, "#home")}
            className="flex items-center"
          >
            <span className="rounded-xl bg-white px-3 py-2 shadow-md">
              <img src="/assets/logo.png" alt="Teo's Painting LLC logo" className="h-11 w-auto sm:h-12" />
            </span>
          </a>

          <nav className="hidden items-center gap-7 md:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="text-[12px] font-bold tracking-[0.18em] text-white/75 transition-colors hover:text-gold"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_HREF}
              data-testid="nav-phone-link"
              className="hidden items-center gap-2 text-sm font-semibold text-white/85 transition-colors hover:text-gold lg:flex"
            >
              <Phone size={16} className="text-gold" />
              {PHONE}
            </a>
            <button
              data-testid="nav-estimate-cta"
              onClick={() => scrollToSection("#contact")}
              className="hidden rounded-full bg-gold px-5 py-2.5 text-[12px] font-extrabold tracking-[0.14em] text-navy transition-[background-color,transform] duration-200 hover:scale-[1.04] hover:bg-gold-light sm:block"
            >
              FREE ESTIMATE
            </button>
            <button
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="rounded-lg p-2 text-white transition-colors hover:text-gold md:hidden"
            >
              <Menu size={26} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="overlay"
              className="fixed inset-0 z-[55] bg-navy-dark/70 backdrop-blur-sm md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              key="drawer"
              data-testid="mobile-menu"
              className="fixed right-0 top-0 z-[60] flex h-full w-[300px] flex-col bg-navy-dark p-8 md:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="self-end rounded-lg p-2 text-white/70 transition-colors hover:text-gold"
              >
                <X size={26} />
              </button>
              <nav className="mt-8 flex flex-col gap-6" aria-label="Mobile">
                {NAV_LINKS.map((l, i) => (
                  <motion.a
                    key={l.href}
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={(e) => go(e, l.href)}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.06 }}
                    className="font-display text-2xl font-bold text-white transition-colors hover:text-gold"
                  >
                    {l.label}
                  </motion.a>
                ))}
              </nav>
              <div className="mt-auto flex flex-col gap-3">
                <a
                  href={PHONE_HREF}
                  data-testid="mobile-call-button"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-5 py-3.5 text-sm font-bold tracking-widest text-white transition-colors hover:border-gold hover:text-gold"
                >
                  <Phone size={16} /> CALL NOW
                </a>
                <button
                  data-testid="mobile-estimate-button"
                  onClick={(e) => go(e, "#contact")}
                  className="rounded-full bg-gold px-5 py-3.5 text-sm font-extrabold tracking-widest text-navy transition-colors hover:bg-gold-light"
                >
                  FREE ESTIMATE
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
